import { useEffect, useRef, useState } from 'react';

export default function CameraCapture({ onCapture, existingFile, label = 'Prendre une photo' }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  /* ── Fix bug caméra ────────────────────────────────────────────────────────
   * srcObject ne peut pas être assigné pendant openCamera() car le <video>
   * n'est pas encore dans le DOM (setOpen(true) n'a pas encore re-rendu).
   * Ce useEffect s'exécute après le rendu, garantissant que videoRef.current
   * est non-null et que la vidéo démarre correctement.
   * ────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (open && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    return () => stopStream();
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const openCamera = async () => {
    setError('');
    stopStream(); // libère le stream précédent avant d'en créer un nouveau

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('La capture photo n\'est pas prise en charge par ce navigateur.');
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = s;
      setOpen(true); // <video> monte → useEffect assigne srcObject
    } catch (err) {
      console.error('getUserMedia error', err);
      setError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
    }
  };

  const closeCamera = () => {
    stopStream();
    setOpen(false);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      setError('La caméra n\'est pas encore prête, patientez.');
      return;
    }
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `owner-live-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
      closeCamera();
    }, 'image/jpeg', 0.92);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
        <button type="button" className="vx-btn vx-btn-ghost" onClick={openCamera}>{label}</button>
        {existingFile && <span style={{ fontSize: '0.95rem' }}>✓ {existingFile.name}</span>}
      </div>
      {error && <p style={{ fontSize: '0.78rem', color: '#dc2626', margin: '0.4rem 0 0' }}>{error}</p>}

      {open && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(10, 15, 40, 0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            width: '100%', maxWidth: 460,
            background: '#ffffff',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          }}>
            {/* Header */}
            <div style={{
              padding: '1rem 1.25rem 0.75rem',
              borderBottom: '1px solid #eef0f5',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#0a1a4f' }}>
                Photo du responsable
              </span>
              <button type="button" onClick={closeCamera} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.4rem', color: '#6e7890', lineHeight: 1,
              }}>×</button>
            </div>

            {/* Vidéo + overlay ovale */}
            <div style={{ position: 'relative', background: '#f0f2f8' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', display: 'block', maxHeight: 340, objectFit: 'cover' }}
              />

              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <svg
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                  viewBox="0 0 460 340"
                  preserveAspectRatio="xMidYMid slice"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <mask id="face-mask-cc">
                      <rect width="460" height="340" fill="white" />
                      <ellipse cx="230" cy="160" rx="100" ry="130" fill="black" />
                    </mask>
                  </defs>
                  <rect width="460" height="340" fill="rgba(0,0,0,0.55)" mask="url(#face-mask-cc)" />
                  <ellipse cx="230" cy="160" rx="100" ry="130" fill="none" stroke="#1a56f0" strokeWidth="2.5" strokeDasharray="8 4" />
                </svg>

                <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center' }}>
                  <span style={{
                    background: 'rgba(10, 26, 79, 0.75)', color: '#fff',
                    fontSize: '0.78rem', fontWeight: 600,
                    padding: '4px 14px', borderRadius: 20, letterSpacing: '0.02em',
                  }}>
                    Positionnez votre visage dans le cadre
                  </span>
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div style={{
              padding: '1rem 1.25rem',
              display: 'flex', gap: '0.75rem', justifyContent: 'flex-end',
              borderTop: '1px solid #eef0f5',
            }}>
              <button type="button" className="vx-btn vx-btn-ghost" onClick={closeCamera}>
                Annuler
              </button>
              <button type="button" className="vx-btn vx-btn-solid" onClick={handleCapture}>
                📸 Capturer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
